import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import * as schoolAdminsApi from '@/services/api/endpoints/schoolAdmins'
import { SchoolAdminDashboardStats, SchoolAdminProfile } from '@/services/api/endpoints/schoolAdmins'
import { ApiError } from '@/services/api/types'
import { RootState } from '@/store/store'

interface SchoolAdminState {
  dashboardStats: SchoolAdminDashboardStats | null
  dashboardStatsLoading: boolean
  error: string | null
  profile: SchoolAdminProfile | null
  profileLoading: boolean
}

const initialState: SchoolAdminState = {
  dashboardStats: null,
  dashboardStatsLoading: false,
  error: null,
  profile: null,
  profileLoading: false,
}

const errorMessage = (error: unknown, fallback: string): string => (error instanceof ApiError ? error.message : fallback)

export const fetchSchoolAdminProfile = createAsyncThunk('schoolAdmin/fetchProfile', async (_, { rejectWithValue }) => {
  try {
    return await schoolAdminsApi.getSchoolAdminProfile()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load school admin profile'))
  }
})

export const fetchSchoolAdminDashboardStats = createAsyncThunk('schoolAdmin/fetchDashboardStats', async (_, { rejectWithValue }) => {
  try {
    return await schoolAdminsApi.getSchoolAdminDashboardStats()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load dashboard stats'))
  }
})

const schoolAdminSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchoolAdminProfile.pending, (state) => {
        state.profileLoading = true
      })
      .addCase(fetchSchoolAdminProfile.fulfilled, (state, action) => {
        state.profile = action.payload
        state.profileLoading = false
      })
      .addCase(fetchSchoolAdminProfile.rejected, (state, action) => {
        state.profileLoading = false
        state.error = action.payload as string
      })
      .addCase(fetchSchoolAdminDashboardStats.pending, (state) => {
        state.dashboardStatsLoading = true
      })
      .addCase(fetchSchoolAdminDashboardStats.fulfilled, (state, action) => {
        state.dashboardStats = action.payload
        state.dashboardStatsLoading = false
      })
      .addCase(fetchSchoolAdminDashboardStats.rejected, (state, action) => {
        state.dashboardStatsLoading = false
        state.error = action.payload as string
      })
  },
  initialState,
  name: 'schoolAdmin',
  reducers: {},
})

export const selectSchoolAdminProfile = (state: RootState): SchoolAdminProfile | null => state.schoolAdmin.profile
export const selectSchoolAdminProfileLoading = (state: RootState): boolean => state.schoolAdmin.profileLoading
export const selectSchoolAdminDashboardStats = (state: RootState): SchoolAdminDashboardStats | null => state.schoolAdmin.dashboardStats
export const selectSchoolAdminDashboardStatsLoading = (state: RootState): boolean => state.schoolAdmin.dashboardStatsLoading

export default schoolAdminSlice.reducer
